import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosProxy } from '../../../api/axiosProxy/axiosProxy';
import { ManifestApiResponse } from '@manifest-cyber/types/interface/apiResponse';
import axios from 'axios';

/* ----------------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------------- */

export interface VulnerabilityAlertConfiguration {
  isActive: boolean;
  alertOnKev: boolean;
  alertOnRecommendation: string[];
  alertOnSeverity: number;
  alertOnExploitability: number; // 0–1 (two‑decimal precision)
  targetEmails: string[];
}

/* ----------------------------------------------------------------------------
 * Hook
 * ------------------------------------------------------------------------- */

export const useVulnerabilityAlertConfig = () => {
  const queryClient = useQueryClient();

  /* ------------------------------ Fetch ---------------------------------- */
  const fetchConfig = useQuery<
    ManifestApiResponse<VulnerabilityAlertConfiguration>,
    Error
  >(['vulnAlertConfig'], async () => {
    try {
      return await AxiosProxy.get({ url: 'vulnerabilities/alert-config' });
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return {
          success: true,
          data: {
            isActive: false,
            alertOnKev: false,
            alertOnRecommendation: [],
            alertOnSeverity: 0,
            alertOnExploitability: 0,
            targetEmails: [],
          },
        };
      }
      throw error;
    }
  });

  /* ------------------------------ Save ----------------------------------- */
  const saveConfig = useMutation<
    ManifestApiResponse<{ message: string }>,
    Error,
    VulnerabilityAlertConfiguration
  >(
    async (config) =>
      AxiosProxy.post({
        url: 'vulnerabilities/alert-config',
        body: config,
      }),
    {
      /* Invalidate cache so UI shows fresh data after a successful save */
      onSuccess: () => queryClient.invalidateQueries(['vulnAlertConfig']),
    },
  );

  return { fetchConfig, saveConfig };
};
